import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { PlatoChip } from "@ats/src/components/shared/PlatoMark";
import SmallButton from "@ats/src/components/shared/SmallButton";
import ManageBillingActions from "@ats/src/views/accountAdmin/accountBilling/ManageBillingActions";
import { formatResetDate } from "./aiSubscriptionHelpers";

// Plato AI subscription status banner — one component for both states (active / none).
interface AiSubscriptionStatusProps {
  isSubscribed: boolean;
  currentCredits: number | null;
  planName?: string | null;
  periodEndsAt?: number | null;
  cancelAtPeriodEnd?: boolean | null;
  cancelAt?: number | null;
  isCanceling?: boolean;
  isRevertingCancellation?: boolean;
  onClickCancel: () => void;
  onClickRevertCancellation: () => void;
}

const AiSubscriptionStatus: React.FC<AiSubscriptionStatusProps> = ({
  isSubscribed,
  currentCredits,
  planName,
  periodEndsAt,
  cancelAtPeriodEnd,
  cancelAt,
  isCanceling = false,
  isRevertingCancellation = false,
  onClickCancel,
  onClickRevertCancellation,
}) => {
  return (
    <Styled.Banner>
      <PlatoChip size={40} radius={10} />
      <Styled.Body>
        {isSubscribed ? (
          <>
            <Styled.Status>Active subscription</Styled.Status>
            <Styled.Title>{planName || "Active"} · {currentCredits?.toLocaleString()} credits per month</Styled.Title>
            {cancelAtPeriodEnd ? (
              <Styled.Description>
                Scheduled to cancel on {formatResetDate(cancelAt)}
              </Styled.Description>
            ) : (
              <Styled.Description>
                Renews {formatResetDate(periodEndsAt)} · unused monthly credits roll over
              </Styled.Description>
            )}
          </>
        ) : (
          <>
            <Styled.Status>No active subscription</Styled.Status>
            <Styled.Title>Subscribe to start reviewing with Plato</Styled.Title>
            <Styled.Description>Recurring monthly credits that roll over.</Styled.Description>
          </>
        )}
      </Styled.Body>
      {isSubscribed && cancelAtPeriodEnd && (
        <SmallButton onClick={onClickRevertCancellation} disabled={isRevertingCancellation} styleType="secondary">
          Don't cancel subscription
        </SmallButton>
      )}
      {isSubscribed && !cancelAtPeriodEnd && (
        <ManageBillingActions returnUrl="/hire/settings/plato-ai/billing" />
      )}
      {!isSubscribed && (
        <ManageBillingActions returnUrl="/hire/settings/billing" />
      )}
    </Styled.Banner>
  );
};

export default AiSubscriptionStatus;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Banner = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionStatus_Banner;
    ${[t.rounded.md, t.mt(4)]}
    padding: 16px 18px 16px 24px;
    border: 1px solid ${t.dark ? t.color.gray[800] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 14px;
    max-width: 696px;

    ${t.mq["md"]} {
      flex-direction: row;
      align-items: center;
    }

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(120deg, #fbd7ff 10%, #ffdec1 90%);
    }
  `;
});

Styled.Body = styled.div(() => css`
  label: AiSubscriptionStatus_Body;
  flex: 1;
`);

Styled.Status = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionStatus_Status;
    font-size: 10px;
    line-height: 14px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Title = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionStatus_Title;
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;
    margin-top: 3px;
    color: ${t.dark ? t.color.gray[100] : t.color.black};
  `;
});

Styled.Description = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionStatus_Description;
    font-size: 12px;
    line-height: 16px;
    margin-top: 3px;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    font-variant-numeric: tabular-nums;
  `;
});

Styled.CancelButton = styled.button((props) => {
  const t: any = props.theme;
  return css`
    label: AiSubscriptionStatus_CancelButton;
    ${[t.text.sm]}
    flex-shrink: 0;
    appearance: none;
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    &:hover {
      text-decoration: underline;
    }
  `;
});
